export const MENU_ITEM_MAX_CHARS = 22
export const MENU_MAX_CUSTOM_ITEMS = 6
export const DEFAULT_DASHBOARD_LABEL = 'Dashboard'
export const DEFAULT_LIBRARY_LABEL = 'Library'
export const DEFAULT_PROFILE_LABEL = 'My Profile'
export const DEFAULT_CUSTOMER_PROFILE_LABEL = 'Customer Profiles'
export const DEFAULT_COMPARISON_LABEL = 'Comparison'
export const DEFAULT_RESOURCES_LABEL = 'Resources'
export const DEFAULT_CALCULATORS_LABEL = 'Calculators'
export const DEFAULT_NEWS_LABEL = 'News'
export const DEFAULT_NAVIGATION_LABEL = 'Visual Navigation'
export const DEFAULT_UGC_LABEL = 'Upload'
export const DEFAULT_SEARCH_LABEL = 'Search'
export const DEFAULT_SHARE_QUEUE_LABEL = 'Share Queue'
export const DEFAULT_REFRESH_LABEL = 'Refresh Content'
export const DEFAULT_SUPPORT_LABEL = 'Support'

/*
 * The standard items in the mp app menu, in the order the app falls back to
 * when a tenant has never reordered them. `id` is what gets stored in
 * `menu.order` / `menu.hidden`; `labelKey` is the `menu.modules` key holding
 * the tenant's label for it (kept as-is for the items that already had one,
 * which is why a few ids and labelKeys disagree).
 *
 * `note` explains a condition the app applies on top of the admin's choice --
 * showing an item here is permission to render it, never a guarantee. Sign Out
 * is deliberately absent: it always renders last and cannot be suppressed.
 */
export const MENU_ITEMS = [
  {
    id: 'search',
    labelKey: 'search',
    name: 'Search',
    icon: 'fa-search',
    defaultLabel: DEFAULT_SEARCH_LABEL,
    note: 'Phone and tablet only — on wide screens the search box sits above the menu',
  },
  {
    id: 'dashboard',
    labelKey: 'dashboard',
    name: 'Dashboard',
    icon: 'fa-dashboard',
    defaultLabel: DEFAULT_DASHBOARD_LABEL,
  },
  {
    id: 'library',
    labelKey: 'library',
    name: 'Library',
    icon: 'fa-book',
    defaultLabel: DEFAULT_LIBRARY_LABEL,
  },
  {
    id: 'news',
    labelKey: 'news',
    name: 'News',
    icon: 'fa-newspaper-o',
    defaultLabel: DEFAULT_NEWS_LABEL,
    note: 'Shown only when the News module is on and news assets exist',
  },
  {
    id: 'resources',
    labelKey: 'resources',
    name: 'Resources',
    icon: 'fa-external-link',
    defaultLabel: DEFAULT_RESOURCES_LABEL,
    note: 'Shown only when resources have been configured',
  },
  {
    id: 'calculators',
    labelKey: 'calculators',
    name: 'Calculators',
    icon: 'fa-calculator',
    defaultLabel: DEFAULT_CALCULATORS_LABEL,
    note: 'Shown only when the user can see published assets in Resources > Calculators',
  },
  {
    id: 'customers',
    labelKey: 'customerProfiles',
    name: 'Customer Profiles',
    icon: 'fa-address-book',
    defaultLabel: DEFAULT_CUSTOMER_PROFILE_LABEL,
    note: 'Shown only when the Customer Profiles module is on',
  },
  {
    id: 'shareQueue',
    labelKey: 'shareQueue',
    name: 'Share Queue',
    icon: 'fa-envelope',
    defaultLabel: DEFAULT_SHARE_QUEUE_LABEL,
    note: 'Shown only when the user has assets queued to share',
  },
  {
    id: 'profile',
    labelKey: 'profile',
    name: 'My Profile',
    icon: 'fa-user',
    defaultLabel: DEFAULT_PROFILE_LABEL,
  },
  {
    id: 'refresh',
    labelKey: 'refresh',
    name: 'Refresh Content',
    icon: 'fa-refresh',
    defaultLabel: DEFAULT_REFRESH_LABEL,
    note: 'On phones and tablets a pull down the menu also refreshes',
  },
  {
    id: 'support',
    labelKey: 'support',
    name: 'Support',
    icon: 'fa-question-circle-o',
    defaultLabel: DEFAULT_SUPPORT_LABEL,
  },
]

export const DEFAULT_MENU_ORDER = MENU_ITEMS.map((item) => item.id)

/*
 * Where custom items sit by default: right after Share Queue, which is where
 * the app has always put them. A tenant that has never reordered anything must
 * not see its custom items move.
 */
export const CUSTOM_ITEMS_DEFAULT_AFTER = 'shareQueue'

const knownOrderWithCustom = (customIds) => {
  const order = DEFAULT_MENU_ORDER.slice()
  const at = order.indexOf(CUSTOM_ITEMS_DEFAULT_AFTER)
  order.splice(at === -1 ? order.length : at + 1, 0, ...customIds)
  return order
}

/*
 * Resolves the stored order into a complete list of ids.
 *
 * A stored order can be stale in both directions: it may omit an item (a
 * release added a menu item after the tenant last saved, or a custom item was
 * added since) and it may name one that no longer exists (a custom item was
 * deleted). So rather than trust it as the whole list, walk the default list --
 * standard items in their shipped order, then the custom items -- and sort by
 * each id's position in the stored order, giving an unlisted id the position of
 * whatever it follows by default. A brand new item therefore lands next to the
 * item it ships beside instead of jumping to the top or bottom.
 *
 * The same resolution runs in mp (src/dux/dam/selectors.js, resolveMenuOrder);
 * keep the two in step.
 */
export const resolveMenuOrder = (storedOrder, customIds = []) => {
  const defaultOrder = knownOrderWithCustom(customIds)
  if (!Array.isArray(storedOrder) || storedOrder.length === 0) {
    return defaultOrder
  }

  let lastKnown = -1
  let tieBreak = 0
  const keyed = defaultOrder.map((id) => {
    const position = storedOrder.indexOf(id)
    if (position === -1) {
      tieBreak += 1
      return { id, position: lastKnown, tieBreak }
    }
    lastKnown = position
    tieBreak = 0
    return { id, position, tieBreak }
  })

  return keyed
    .sort((a, b) =>
      a.position === b.position
        ? a.tieBreak - b.tieBreak
        : a.position - b.position,
    )
    .map((entry) => entry.id)
}
